import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Loader2 } from "@/components/icons";
import { supabase } from "@/lib/supabase";
import { usePostSignupRedirect } from "@/billing/usePostSignupRedirect";
import { recordConsent } from "@/api/consent";
import { PENDING_CONSENT_KEY } from "@/legal/version";
import { ResendVerificationForm } from "./ResendVerificationForm";

export function ConfirmHandler() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isAuthed, setIsAuthed] = useState(false);
  const { t } = useTranslation("auth");

  usePostSignupRedirect(isAuthed);

  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        setIsAuthed(true);
        // Flush any pending OAuth-path consent (set by GoogleButton).
        const pending = localStorage.getItem(PENDING_CONSENT_KEY);
        if (pending) {
          localStorage.removeItem(PENDING_CONSENT_KEY);
          recordConsent(pending).catch((err: unknown) => {
            console.warn("recordConsent failed", err);
            Sentry.captureException(err, {
              tags: { area: "legal", path: "consent-flush" },
              extra: { pending_version: pending },
            });
          });
        }
        navigate("/dashboard", { replace: true });
      } else if (event === "PASSWORD_RECOVERY") {
        navigate("/auth/reset-password", { replace: true });
      }
    });
    // Failure mode: hash present but no auth event after a few seconds.
    const timeout = setTimeout(() => {
      if (!window.location.hash) {
        setError(t("confirm.noToken"));
      } else {
        setError(t("confirm.linkExpired"));
      }
    }, 5000);
    return () => {
      subscription.subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate, t]);

  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <ResendVerificationForm />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{t("confirm.spinner")}</span>
    </div>
  );
}
